import { authOptions } from "@bppages/api/auth/[...nextauth].page";
import { NextPageWithLayout } from "@bptypes/layout";
import { Box } from "@components/core/Box";
import { PageHeading } from "@components/headings";
import { SubscriptionPlans } from "@components/pages/subscriptions";
import { Spacer } from "@components/spacer";
import { Stepper } from "@components/stepper";
import { MainLayout } from "@layouts/index";
import { DEFAULT_LOCALE } from "@lib/constants";
import { AVAILABLE_PLANS, PLAN_ORDER } from "@lib/constants/subscriptions";
import { MY_SUBSCRIPTIONS_URL } from "@lib/constants/urls";
import { exportAnonSession, useSessionContext } from "@lib/context/session";
import { getMySubscriptionsQuery } from "@lib/network/my";
import { getPlansQuery } from "@lib/network/plans";
import { checkIfUserHasSubscription, getCurrencyFromPlan } from "@lib/utils";
import { getLocationData } from "@lib/utils/getLocationData";
import isClient from "@lib/utils/isClient";
import { DehydratedState, QueryClient, dehydrate, useQuery } from "@tanstack/react-query";
import { GetServerSideProps, GetServerSidePropsContext, GetServerSidePropsResult } from "next";
import { getServerSession } from "next-auth";
import { useTranslation } from "next-i18next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { useRouter } from "next/router";
import { ReactElement, useEffect, useState } from "react";
import styled from "styled-components";

export const StyledBoxWrapper = styled.div`
	> div {
		display: inline-block;
		max-width: 35rem;

		a {
			text-decoration: underline;
			color: ${(props) => props.theme.colors.neutrals.secondary.gray.indicator2};
			font-weight: normal;
		}
	}
`;

interface Props {
	mySubscription: MySubscription;
	countryCode: string;
	anonSession: AnonSession;
}

const Subscriptions: NextPageWithLayout<Props> = ({ mySubscription, countryCode, anonSession }) => {
	// we need session to determine if we are showing user subscriptions or subscriptions page
	const { getAccessToken, getIsSessionValid, session, importAnonSession } = useSessionContext();
	importAnonSession(anonSession);
	const introspectUser = session?.introspect;
	const accessToken = getAccessToken();
	const isSessionValid = getIsSessionValid();
	const isLoggedInSessionValid = getIsSessionValid({ isAnonAllowed: false });
	const { t } = useTranslation("translation");
	const [mounted, setMounted] = useState(false);
	const router = useRouter();
	const { onboarding } = router.query;
	const [plans, setPlans] = useState<Array<Plan>>([]);
	const currency = getCurrencyFromPlan(plans);
	const [currentSubscription, setCurrentSubscription] = useState(mySubscription);

	if (isClient() && checkIfUserHasSubscription(introspectUser)) {
		router.push(MY_SUBSCRIPTIONS_URL);
	};

	const { data: plansData } = useQuery(
		{
			...getPlansQuery({ accessToken, countryCode }),
			enabled: isSessionValid,
		},
	);

	const { data: subscription } = useQuery({
		...getMySubscriptionsQuery({
			accessToken,
			personId: session?.introspect?.person_id,
		}),
		enabled: isLoggedInSessionValid,
	});

	useEffect(() => {
		setCurrentSubscription(subscription);
	}, [subscription]);

	useEffect(() => {
		if (plansData) {
			const availablePlans = plansData
				.slice()
				.sort(
					(a: Plan, b: Plan) =>
						PLAN_ORDER[a.plan_code] - PLAN_ORDER[b.plan_code],
				)
				.filter((plan: Plan) => AVAILABLE_PLANS.includes(plan.plan_code));
			setPlans(availablePlans);
		}
	}, [plansData]);

	const handleSkip = () => {
		router.push("/");
	};
	const handleBack = () => {
		router.back();
	};

	useEffect(() => {
		setMounted(true);
	}, []);

	if (!mounted) return <></>;

	return (
		<>
			{onboarding && (
				<Stepper
					steps={3}
					currentStep={3}
					onBack={handleBack}
					backText={`< ${t("Actions.Back")}`}
					onSkip={handleSkip}
					skipText={t("SkipFinish")}
				/>
			)}
			<PageHeading
				title={t("Subscriptions.AddStreaming")}
				subtitle={(
					<StyledBoxWrapper>
						<Box alignItems="center" text="secondary">
							{t("Subscriptions.StreamingCTA")}
							{" "}
							<a
								href="https://stream.beatport.com/"
								target="_blank"
								rel="noreferrer"
							>
								{t("Subscriptions.LearnMoreStreaming")}
							</a>
						</Box>
					</StyledBoxWrapper>
				)}
			/>
			<Spacer h="8" />
			<SubscriptionPlans
				currency={currency}
				plans={plans}
				mySubscription={currentSubscription}
			/>
		</>
	);
};

export default Subscriptions;

const PageLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const { t } = useTranslation("translation");

	return (
		<MainLayout
			title={t("Subscriptions.LearnMoreStreaming")}
			metaDescription={t("Subscriptions.CheckoutAvailable")}
			hasDesktopSidebar={false}
		>
			{children}
		</MainLayout>
	);
};

Subscriptions.getLayout = (page: ReactElement<Props>) => (
	<PageLayout>
		{page}
	</PageLayout>
);

type ServerSideProps = {
	mySubscription: MySubscription;
	countryCode: string;
	dehydratedState: DehydratedState;
	anonSession: AnonSession;
};

export const getServerSideProps: GetServerSideProps<ServerSideProps> = async (
	ctx: GetServerSidePropsContext,
): Promise<GetServerSidePropsResult<ServerSideProps>> => {
	const queryClient = new QueryClient();
	const session = await getServerSession(ctx.req, ctx.res, authOptions);

	const accessToken = session?.token.accessToken || "";

	const location = getLocationData(ctx.req);

	// fetch plans from API
	await queryClient
		.fetchQuery(getPlansQuery({ accessToken, location }))
		.then((res) => res)
		.catch(() => { });

	const mySubscription = await queryClient
		.fetchQuery(getMySubscriptionsQuery({
			accessToken,
			location,
			personId: session?.introspect?.person_id,
		}))
		.then((res) => res)
		.catch(() => { });

	const plan_code = mySubscription?.subscription?.bundle?.plan_code;
	const isExpired = mySubscription?.status?.indexOf("expired") > -1;
	const hasYearlyParam = ctx.query?.hasOwnProperty("yearly");

	// user has subscription and it is not expired redirect to my subscriptions
	if (plan_code && !isExpired) {
		const localeCookie = ctx.req.cookies["NEXT_LOCALE"];
		let destination = hasYearlyParam ? "/my-subscriptions?yearly" : "/my-subscriptions";
		if (localeCookie) destination = hasYearlyParam ? `/${localeCookie}/my-subscriptions?yearly` : `/${localeCookie}/my-subscriptions`;
		return {
			redirect: {
				permanent: false,
				destination,
			},
		};
	}

	const anonSession = await exportAnonSession();

	return {
		props: {
			mySubscription,
			countryCode: location?.countryCode ?? "",
			dehydratedState: dehydrate(queryClient),
			anonSession,
			...(await serverSideTranslations(ctx.locale ?? DEFAULT_LOCALE, ["translation"])),
		},
	};
};
